import React, { useState, useCallback, useMemo } from 'react';

const UrlMultiInput = ({
  label = 'Paste multiple URLs (one per line):',
  description = 'Press Enter after each URL or paste comma/newline separated lists.',
  placeholder = 'Paste URLs here...',
  rows = 3,
  urls,
  setUrls,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [isComposing, setIsComposing] = useState(false);

  const normalizeUrl = useCallback((raw) => {
    const trimmed = raw?.trim();
    if (!trimmed) return '';
    if (/^https?:\/\//i.test(trimmed)) {
      return trimmed;
    }
    if (/^\/\//.test(trimmed)) {
      return `https:${trimmed}`;
    }
    return `https://${trimmed}`;
  }, []);

  const extractUrls = useCallback((text) => {
    return text
      .split(/[\s,]+/)
      .map(part => normalizeUrl(part))
      .filter(Boolean);
  }, [normalizeUrl]);

  const addUrls = useCallback((text) => {
    if (!text?.trim()) return;
    const candidates = extractUrls(text);
    if (!candidates.length) return;
    setUrls(prevUrls => {
      const existing = new Set(prevUrls.map(url => url.toLowerCase()));
      const next = [...prevUrls];
      candidates.forEach(candidate => {
        const key = candidate.toLowerCase();
        if (!existing.has(key)) {
          existing.add(key);
          next.push(candidate);
        }
      });
      return next;
    });
  }, [extractUrls, setUrls]);

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey && !isComposing) {
      event.preventDefault();
      if (inputValue.trim()) {
        addUrls(inputValue);
        setInputValue('');
      }
    }
  };

  const handleBlur = () => {
    if (inputValue.trim()) {
      addUrls(inputValue);
      setInputValue('');
    }
  };

  const handleChange = (event) => {
    setInputValue(event.target.value);
  };

  const helperText = useMemo(() => {
    if (!urls?.length) {
      return description;
    }
    return `Adding to ${urls.length} existing URL${urls.length === 1 ? '' : 's'}.`;
  }, [description, urls]);

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label}
      </label>
      <textarea
        value={inputValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        onCompositionStart={() => setIsComposing(true)}
        onCompositionEnd={() => setIsComposing(false)}
        placeholder={placeholder}
        rows={rows}
        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
      />
      <p className="text-xs text-gray-500 mt-2">{helperText}</p>
    </div>
  );
};

export default UrlMultiInput;

